import { ButtonMenu } from '@/components/Elements/ButtonMenu';
import { PageTitle } from '@/components/Layout/PageTitle';
import { SectionTitle } from '@/components/Layout/SectionTitle';
import { DomainUser } from '@/hooks/useIntrospect';
import { Transition } from '@headlessui/react';
import RequestStatusPill from '@oncolens/oncolens-ui/lib/components/Collaboration/RequestStatusPill';
import withLoading from '@oncolens/oncolens-ui/lib/components/Common/withLoading';
import { EventDto } from '@oncolens/oncolens-ui/lib/dto/event/Event';
import { CollaborationTemplateDto, RequestStatus } from '@oncolens/oncolens-ui/lib/dto/event/request';
import { dueInText } from '@oncolens/oncolens-ui/lib/utilities/dates';
import serviceUrls from '@oncolens/oncolens-ui/lib/api/service-urls';
import React, { useCallback } from 'react';
import { FaChevronDown, FaEdit, FaFileDownload, FaPrint } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '@/features/auth/stores/authStore';
import { useGetTemplate } from "@/features/requests/api/getTemplate";

interface RequestInformationProps {
	requestId: string;
	request: EventDto;
  templateId: string;
}

export const RequestInformation = ({ requestId, request, templateId }: RequestInformationProps) => {
  const editUri = `/newrequest/${templateId}?requestId=${requestId}`;
  const domainUser: DomainUser | null = useAuthStore((x) => x.domainUser);
  const orgName = domainUser?.orgRoles?.find(
    (x) => x.organizationId === request?.organizationId
  )?.orgName;
  const isLoading = !domainUser && !request;
  const { data: template } = useGetTemplate(
    templateId || ''
  );
  // TODO: Add these as Workflow options instead of looking for an OSU Template
  const isOsuTemplate = template?.component === "OsuFormTemplate";
  const isPrintable =
    (request?.status === 'Report Ready' || request?.status === 'Report Received') && !isOsuTemplate;
  const isEditable = request?.status !== 'Report Ready' && request?.status !== 'Report Received';
  const isDownloadable = request?.status === 'Report Ready' && isOsuTemplate;

  const showEdit = isPrintable || isEditable || isDownloadable;
  
  const editButtonNode = () => {
    return <>
      {showEdit && <EditButton
        requestId={requestId}
        editUri={editUri}
        isPrintable={isPrintable}
        isLoading={isLoading}
        isEditable={isEditable}
        isDownloadable={isDownloadable}
      />}
    </>
  }

  return (
    <>
      <PageTitle
        title="View Request"
        className="print:hidden"
        middleContent={
          <TitleStatus status={request?.status as RequestStatus} isLoading={isLoading} />
        }
        rightContent={editButtonNode()}
      />
      {request?.displayInternalId && (
        <p className="mt-0 mb-3 text-lg text-gray-800">
					Please record the following submission ID:{' '}
          <strong data-cy="request-information-internalid">{request?.displayInternalId}</strong>
        </p>
      )}
      <SectionTitle title="Request Details" className="hidden print:block" />
      <RequestInformationBodyWithLoading
        orgName={orgName}
        request={request}
        isLoading={isLoading}
      />
    </>
  );
};

const RequestInformationSkeleton = () => (
  <>
    <div className="flex mb-4">
      <dl className="flex-1 mx-3">
        <div className="py-0.5">
          <dt className="block h-5 bg-gray-300 rounded-lg w-52 animate-pulse"></dt>
        </div>
        <div className="py-0.5">
          <dt className="block h-5 bg-gray-300 rounded-lg w-52 animate-pulse"></dt>
        </div>
        <div className="py-0.5">
          <dt className="block h-5 bg-gray-300 rounded-lg w-52 animate-pulse"></dt>
        </div>
      </dl>
      <dl className="flex-1 mx-3">
        <div className="py-0.5">
          <dt className="block w-40 h-5 bg-gray-300 rounded-lg animate-pulse"></dt>
        </div>
        <div className="py-0.5">
          <dt className="block w-32 h-5 bg-gray-300 rounded-lg animate-pulse"></dt>
        </div>
      </dl>
    </div>
  </>
);

interface RequestInformationBodyProps {
	orgName: string | undefined;
	request: EventDto;
}

const RequestInformationBody = ({ orgName, request }: RequestInformationBodyProps) => {
  return (
    <div className="flex mb-4" data-cy="request-information">
      <dl className="hidden mx-3 print:block print:w-1/2">
        <div>
          <dt className="inline-block mr-2 font-bold">Request: </dt>
          <dd className="inline-block font-bold">{request?.displayInternalId}</dd>
        </div>
        <div>
          <dt className="inline mr-1 font-bold">Age: </dt>
          <dd className="inline">{request?.patient?.age}</dd>
        </div>
        <div>
          <dt className="inline mr-1 font-bold">Race: </dt>
          <dd className="inline">
            {request?.patient?.raceConfig
              ? request?.patient?.raceConfig?.value
              : 'Not Specified'}
          </dd>
        </div>
        <div>
          <dt className="inline mr-1 font-bold">Sex: </dt>
          <dd className="inline">{request?.patient?.sexConfig?.value}</dd>
        </div>
      </dl>
      <dl className="flex-1 mx-3 print:w-1/2">
        <div className="print:hidden">
          <dt className="inline-block mr-2 font-bold">Submitted to: </dt>
          <dd className="inline-block" data-cy="request-information-org-name">
            {orgName}
          </dd>
        </div>
        <div className="hidden print:block">
          <dt className="inline-block mr-2 font-bold">Reason for request: </dt>
          <dd className="inline-block print:block">{request?.topicForDiscussion}</dd>
        </div>
        <div>
          <dt className="inline-block mr-2 font-bold">Requested by: </dt>
          <dd className="inline-block" data-cy="request-information-requested-by">
            {request?.createdByDisplayName ? request?.createdByDisplayName : '-'}
          </dd>
        </div>
        <div>
          <dt className="inline-block mr-2 font-bold">Requested on: </dt>
          <dd className="inline-block" data-cy="request-information-requested-on">
            {request?.createdDate && new Date(request.createdDate).toLocaleDateString()}
          </dd>
        </div>
      </dl>
      <dl className="flex-1 mx-3 print:hidden">
        <div>
          <dt className="inline-block mr-2 font-bold">Due Date: </dt>
          <dd className="inline-block" data-cy="request-information-due-date">
            {request?.dueDate ? dueInText(new Date(request?.dueDate)) : null}
          </dd>
        </div>
        <div>
          <dt className="inline-block mr-2 font-bold">Priority: </dt>
          <dd className="inline-block" data-cy="request-information-priority">
            {request?.priority}
          </dd>
        </div>
      </dl>
    </div>
  );
};

const RequestInformationBodyWithLoading = withLoading(
  RequestInformationBody,
  RequestInformationSkeleton
);

interface TitleStatusProps {
	isLoading: boolean;
	status: string;
}

const TitleStatus = ({ status, isLoading }: TitleStatusProps) => (
  <div className="relative">
    <Transition
      className="ml-4"
      enter="transition-opacity duration-300 delay-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      show={!isLoading}
    >
      <RequestStatusPill status={status as RequestStatus} />
    </Transition>
    <Transition
      enter="transition-opacity"
      leave="transition-opacity absolute left-0 top-1/2 transform -translate-y-1/2 duration-300"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      show={isLoading}
    >
      <RequestStatusPillSkeleton />
    </Transition>
  </div>
);

const RequestStatusPillSkeleton = () => (
  <span className="block w-24 h-5 ml-4 bg-gray-300 rounded-full animate-pulse"></span>
);

interface EditButtonProps {
	editUri: string;
	isPrintable: boolean;
  isDownloadable: boolean;
  isEditable: boolean;
	isLoading: boolean;
	requestId: string;
}

const EditButton = ({ editUri, isPrintable, isLoading, requestId, isEditable, isDownloadable }: EditButtonProps) => {
  const navigate = useNavigate();

  const onDownloadHandle = useCallback(() => {
    window.open(
      `${serviceUrls.CORE_DOMAIN_API.BASE()}/api/collaborationrequests/${requestId}/download`
    );
  }, [requestId]);

  const menuItems = [];
  if (isEditable) {
    menuItems.push( {
      label: 'Edit Request',
      onClick: () => navigate(editUri),
      icon: {
        startIcon: <FaEdit className="mr-2" />
      }
    });
  }
  
  if (isPrintable) {
    menuItems.push({
      label: 'Print',
      onClick: () => window.print(),
      icon: {
        startIcon: <FaPrint className="mr-2" />
      }
    });
  }
  
  if (isDownloadable) {
    menuItems.push({
        label: 'Download Referral Form',
        onClick: onDownloadHandle,
        icon: {
          startIcon: <FaFileDownload className="mr-2" />
        }
      });
  }

  return (
    <ButtonMenu
      disabled={isLoading}
      menuItems={menuItems}
      endIcon={<FaChevronDown />}
      size="sm"
      alignment="right"
      className="print:hidden"
    >
			Actions
    </ButtonMenu>
  );
};
